// Story 5.4 — Marketplace listing card. Renders both share-side and
// sealed-dossier-side listings via a kind discriminant.
//
// Epic 8 design pass (2026-08), corrected against the real design
// templates (design/claude-design/templates/market-page/MarketPage.dc.html):
// the market is a LIGHT paper surface. Each listing is a paper card
// (paper-50 ground, paper-300 hairline, 8px radius) that leads with the
// ARTIFACT as its centered hero — the real <SealedDossier> parchment
// gatefold for sealed-dossier listings, and a certificate-styled framed
// vignette (tier-accented frame + creator vignette) for opened-share
// listings. Below the hero: name + tier badge (shares) or name + "Sealed"
// badge (dossiers), a mono muted provenance line, then a hairline-topped
// bottom row with the mono semibold ask price left and a "View listing →"
// affordance right (rendered as text — the whole card stays one <button>).
// Values full-contrast, labels muted (13-patterns contrast law); no hover
// motion.
//
// Share hero: the real <PatronageCertificate> (engraved-parchment resale
// artifact from the design tool), rendered as the full 320×452 intrinsic
// artifact scaled 0.5 (→ 160×226) into the 226px hero well, per the Market
// card spec ("render your cert component scaled — don't rebuild a
// thumbnail"). It is non-interactive (no nested links/buttons) so it nests
// safely inside this card's <button>. Epic 8: the browse row
// (MarketplaceShareCardData) now carries the full certificate face — tier,
// GH- serial (from the share uuid), dossier (portfolio_title), issued
// (opened_at), and the condition + signed cosmetic axes — so the cert renders
// complete (only the creator quote stays omitted on the compact hero, matching
// the vault thumbnail). The sealed side has every field <SealedDossier> needs,
// so it renders the real artifact (mirrors the design's SealedDossierV2 usage).
//
// The card stays a focusable <button> (opens the buy confirm-modal) per
// epic AC §1697; the artifacts render non-interactively so no interactive
// element nests inside the button.
//
// Amended 2026-06-13 (MiFID II legal-defensive decision #1): price display
// is collectible-market ORIENTATION, not investment SIGNAL. The lowest-listing
// reference renders neutral "Lowest listing" / "Listings from €X" copy — NOT
// an "↑ €X above floor" appreciation/spread cue (no arrow glyph, no amber).
// The backend delta_vs_floor_cents / is_floor fields are unchanged.

import {
  Badge,
  PatronageCertificate,
  SealedDossier,
  formatIssueNumber,
  isLoadableImageUrl,
  type PatronageCertificateProps,
  type SealedDossierData,
} from "@greenhold/components";

import { certSerialFromShareId } from "../../lib/certSerial";
import { formatCents } from "./feeMath";
import type {
  MarketplaceShareCardData,
  MarketplaceShareListingRow,
} from "./useMarketplaceShareListings";
import type { MarketplaceSealedDossierListingRow } from "./useMarketplaceSealedDossierListings";

export type MarketplaceListingCardListing =
  | { kind: "share"; row: MarketplaceShareListingRow }
  | { kind: "sealed_dossier"; row: MarketplaceSealedDossierListingRow };

export interface MarketplaceListingCardProps {
  listing: MarketplaceListingCardListing;
  onClick: (listing: MarketplaceListingCardListing) => void;
}

/** Normalizes an app tier string onto the <PatronageCertificate> tier
 *  vocabulary. The marketplace/vault wire tiers are already lowercase
 *  ("common"|"preferred"|"premium"|"founders"), but this maps defensively
 *  (lowercase + known synonyms) so any upstream casing/vocabulary drift
 *  resolves to the correct palette instead of silently falling back to the
 *  near-ink common ramp — the tier ink + accent glow depend on it. */
export function normalizeCertTier(raw: string): PatronageCertificateProps["tier"] {
  switch (raw.trim().toLowerCase()) {
    case "preferred":
      return "preferred";
    case "premium":
      return "premium";
    case "founders":
    case "founder":
    case "founder's":
    case "founders'":
      return "founders";
    case "common":
    default:
      return "common";
  }
}

/** Tier main-ink colors from the PatronageCertificate palette (Certificate
 *  Options design) — the card's creator name is inked in the tier color so
 *  the tier reads through the name itself, matching the cert face. */
const TIER_INK: Record<PatronageCertificateProps["tier"], string> = {
  common: "#1f2d24",
  preferred: "#2a5da0",
  premium: "#5b21b6",
  founders: "#c39235",
};

/** Maps a marketplace share-listing row onto the <PatronageCertificate>
 *  props. Epic 8: the browse row is no longer a thin projection — it carries
 *  all five certificate data points, so the cert face renders in full:
 *  tier, GH- serial (derived from the share uuid), dossier ← portfolio_title,
 *  issued ← opened_at (date part), condition + signed (cosmetic axes). The
 *  creator quote is still omitted on the compact market/vault heroes (parity
 *  with the vault thumbnail; the full quote reads on ShareDetail). Exported
 *  for reuse by BuyListingConfirmModal's dark display inset. */
export function certPropsFromShareRow(
  share: MarketplaceShareCardData,
): PatronageCertificateProps {
  const rawImageUrl = share.creator_image_signed_url;
  const portraitUrl = isLoadableImageUrl(rawImageUrl) ? rawImageUrl : null;
  return {
    tier: normalizeCertTier(share.tier),
    name: share.creator_display_name,
    serial: certSerialFromShareId(share.share_id),
    dossier: share.portfolio_title,
    issued: share.opened_at.slice(0, 10),
    condition: share.condition,
    signed: share.signed,
    portraitUrl,
  };
}

function daysAgo(iso: string): number {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const days = Math.max(0, Math.floor((now - then) / (1000 * 60 * 60 * 24)));
  return days;
}

function daysUntil(iso: string): number {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const days = Math.max(0, Math.ceil((then - now) / (1000 * 60 * 60 * 24)));
  return days;
}

/** Projects a marketplace sealed-dossier listing row onto the
 *  SealedDossierData shape the <SealedDossier> component consumes.
 *  Exported for reuse by BuyListingConfirmModal's display inset. */
export function dossierFromListingRow(
  row: MarketplaceSealedDossierListingRow,
): SealedDossierData {
  return {
    id: row.sealed_dossier.sealed_dossier_id,
    drop_id: row.sealed_dossier.drop_id,
    edition_number: row.sealed_dossier.edition_number,
    portfolio_title: row.sealed_dossier.portfolio_title,
    drop_status: row.sealed_dossier.drop_status,
    retiring_at: row.sealed_dossier.drop_retiring_at,
    seal_color: row.sealed_dossier.seal_color,
  };
}

// When this listing IS the lowest current ask for its bucket, the flag is
// rendered as a classic 45° corner-ribbon banner across the card's top-right
// corner (gold treatment — reserved for prestige/provenance) rather than a
// pill in the data area. The clip wrapper carries its own overflow-hidden so
// the parent card can keep visible overflow (the certificate glow needs it);
// pointer-events-none keeps it from eating the card's click.
function LowestListingRibbon() {
  // Classic 45° corner-ribbon geometry. The clip wrapper is a 96×96 square
  // over the card's top-right corner (overflow-hidden). The strip is 140px
  // wide — comfortably past the wrapper's 96·√2 ≈ 136px diagonal — so it spans
  // the full corner, and it is positioned (right:-40 / top:22 / rotate 45deg)
  // so the strip's CENTER sits on the corner diagonal. "Lowest listing" at 9px
  // mono is ~80px, centered in the 140px strip, so it clears both clipped tips
  // by ~30px — no glyph (not even the leading "L") falls outside the clip.
  return (
    <div
      className="pointer-events-none absolute right-0 top-0 z-10 h-[96px] w-[96px] overflow-hidden rounded-tr-lg"
    >
      <span
        data-testid="lowest-listing-tag"
        className="absolute right-[-40px] top-[22px] w-[140px] rotate-45 bg-brand-gold-500 py-[3px] text-center font-mono text-[9px] font-semibold uppercase tracking-[0.08em] text-brand-ink-900 shadow-sm"
      >
        Lowest listing
      </span>
    </div>
  );
}

// Non-floor listings surface a neutral collectible-market orientation pill in
// the data area — the bucket's lowest current ask ("Listings from €X"), NOT
// how far above floor this one is (MiFID II legal-defensive decision).
function LowestListingRef({
  priceCents,
  deltaCents,
}: {
  priceCents: number;
  deltaCents: number;
}) {
  const floorCents = priceCents - deltaCents;
  return (
    <span
      data-testid="lowest-listing-ref"
      className="inline-flex items-center gap-1 rounded-full border border-brand-paper-300 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-brand-forest-900/60"
    >
      Listings from {formatCents(floorCents)}
    </span>
  );
}

export function MarketplaceListingCard({
  listing,
  onClick,
}: MarketplaceListingCardProps) {
  const isShare = listing.kind === "share";
  const row = listing.row;
  const listedDays = daysAgo(row.listed_at);

  // "Issue" is the consumer-facing label for edition_number (2026-08 copy
  // rename; API field + code identifiers keep the edition_ vocabulary).
  const editionLabel = isShare
    ? `Issue #${(row as MarketplaceShareListingRow).share.edition_number}`
    : `Issue #${(row as MarketplaceSealedDossierListingRow).sealed_dossier.edition_number}`;
  const title = isShare
    ? (row as MarketplaceShareListingRow).share.creator_display_name
    : (row as MarketplaceSealedDossierListingRow).sealed_dossier.portfolio_title;

  // Provenance — mono, muted (labels-muted law; the ask price below is the
  // full-contrast value). Shares stack THREE stacked data points (edition /
  // originating dossier / condition float) rather than one dot-joined line, so
  // a long portfolio_title no longer wrecks the compact card meta; the dossier
  // line truncates with an ellipsis instead. Sealed keep their short single
  // dot-joined line (edition + drop lifecycle status).
  const sealedProvenance = isShare
    ? ""
    : `Issue #${formatIssueNumber((row as MarketplaceSealedDossierListingRow).sealed_dossier.edition_number)} · Drop status: ${(row as MarketplaceSealedDossierListingRow).sealed_dossier.drop_status}`;

  return (
    <button
      type="button"
      onClick={() => onClick(listing)}
      aria-label={`Listing: ${title}, ${editionLabel}, ${formatCents(row.price_cents)}${row.is_floor ? ", lowest listing" : ""}`}
      className="relative flex h-full w-full flex-col items-center gap-3 rounded-lg border border-brand-paper-300 bg-brand-paper-200 p-4 text-center text-brand-forest-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500 focus-visible:ring-offset-2"
    >
      {/* Lowest-listing flag — corner ribbon (gold), only when this listing
          is its bucket's floor. Sits above the hero; clipped to the corner. */}
      {row.is_floor && <LowestListingRibbon />}

      {/* "Your listing" marker (top-left, clear of the top-right ribbon) so a
          seller recognizes their own listing before opening it — the buy +
          make-an-offer actions are hidden in the confirm modal. */}
      {row.is_own_listing && (
        <span
          data-testid="your-listing-badge"
          className="pointer-events-none absolute left-3 top-3 z-10 rounded-full bg-brand-forest-700 px-2 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.08em] text-brand-paper-100"
        >
          Your listing
        </span>
      )}

      {/* Hero artifact, centered in a consistent-height well — the design's
          card anatomy (226px well; cert vignette / sealed dossier both live
          in it so cards sit on a shared baseline). */}
      <div className="flex h-[226px] items-center justify-center">
        {isShare ? (
          // Full 320×452 certificate scaled 0.5 → 160×226, filling the well.
          <PatronageCertificate
            width={160}
            {...certPropsFromShareRow(
              (row as MarketplaceShareListingRow).share,
            )}
          />
        ) : (
          // Same 160px footprint as the share certificate (A-series 1.414
          // ratio → ~226 tall) so both tabs sit on one baseline — shares
          // fit INSIDE dossiers, so the dossier must never render smaller.
          // size="md" (face cap 240) lets the face fill the full 160;
          // "thumb" would cap it at 96 and leave it off-center.
          <div className="w-[160px]">
            <SealedDossier
              dossier={dossierFromListingRow(
                row as MarketplaceSealedDossierListingRow,
              )}
              size="md"
              state={
                (row as MarketplaceSealedDossierListingRow).sealed_dossier
                  .drop_status === "retired"
                  ? "retired"
                  : "idle"
              }
              showRetirementWarning={
                (row as MarketplaceSealedDossierListingRow).sealed_dossier
                  .drop_status === "retiring"
              }
            />
          </div>
        )}
      </div>

      {/* Name row (design card anatomy): certificate listings show the
          creator name inked in the tier's main color — the tier reads
          through the color (matching the cert face) instead of a badge
          pill; sealed listings show portfolio name + "Sealed" secondary
          badge. */}
      <div className="flex flex-wrap items-center justify-center gap-2">
        {isShare ? (
          <span
            className="font-heading text-sm font-semibold"
            style={{
              color:
                TIER_INK[
                  normalizeCertTier(
                    (row as MarketplaceShareListingRow).share.tier,
                  )
                ],
            }}
          >
            {title}
          </span>
        ) : (
          <>
            <span className="font-heading text-sm font-semibold">{title}</span>
            <Badge variant="secondary">Sealed</Badge>
          </>
        )}
      </div>

      {/* Provenance — mono muted meta. Shares stack three data points
          (edition / dossier / condition) so a long dossier title truncates on
          its own line instead of blowing out a single dot-joined string;
          sealed keep their short single line. */}
      {isShare ? (
        <div className="flex w-full min-w-0 flex-col items-center gap-0.5 font-mono text-[11px] uppercase leading-tight tracking-wider text-brand-forest-900/55">
          <span>
            Issue #
            {formatIssueNumber(
              (row as MarketplaceShareListingRow).share.edition_number,
            )}
          </span>
          <span className="w-full min-w-0 truncate">
            {(row as MarketplaceShareListingRow).share.portfolio_title}
          </span>
          <span>
            {(row as MarketplaceShareListingRow).share.condition.toFixed(8)}
          </span>
        </div>
      ) : (
        <span className="font-mono text-[11px] uppercase tracking-wider text-brand-forest-900/55">
          {sealedProvenance}
        </span>
      )}

      {/* Lowest-listing orientation (neutral, MiFID II) — the floor case is
          the corner ribbon above; only non-floor listings get the data-area
          "Listings from €X" reference pill. */}
      {!row.is_floor && (
        <LowestListingRef
          priceCents={row.price_cents}
          deltaCents={row.delta_vs_floor_cents}
        />
      )}

      {/* Bottom row — hairline top, mono price left, view affordance right */}
      <div className="mt-auto flex w-full items-baseline justify-between gap-2 border-t border-brand-paper-200 pt-2.5">
        <span className="font-mono text-base font-semibold text-brand-forest-900">
          {formatCents(row.price_cents)}
        </span>
        <span className="text-sm text-brand-forest-600">View listing →</span>
      </div>

      {/* Footer meta — muted labels */}
      <div className="flex w-full flex-col gap-0.5 text-left text-[11px] text-brand-forest-900/55">
        <span>
          Listed {listedDays === 0 ? "today" : `${listedDays}d ago`}
          {row.expires_at ? ` · Expires in ${daysUntil(row.expires_at)}d` : ""}
        </span>
        {!row.seller_anonymous && row.seller_handle && (
          <span className="text-brand-forest-900/75">@{row.seller_handle}</span>
        )}
      </div>
    </button>
  );
}
